import { Check, Download, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/Badge';
import {
  useDownloadRegulatorySubmission,
  useReviewRegulatorySubmission,
  useRegulatoryPendingSubmissions,
  type RegulatoryBundleStatus,
} from '../hooks/useAdminData';

function statusBadgeVariant(status: RegulatoryBundleStatus): 'warning' | 'success' | 'danger' {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'warning';
  }
}

function formatSubmittedAt(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function RegulatoryApprovals() {
  const { data, isLoading } = useRegulatoryPendingSubmissions();
  const review = useReviewRegulatorySubmission();
  const download = useDownloadRegulatorySubmission();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reviewerNote, setReviewerNote] = useState('');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );

  function showNotification(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }

  function handleApprove(id: string) {
    review.mutate(
      { id, status: 'approved' },
      {
        onSuccess: () => showNotification('success', 'Submission approved'),
        onError: () => showNotification('error', 'Failed to approve submission'),
      },
    );
  }

  function startReject(id: string) {
    setRejectingId(id);
    setReviewerNote('');
  }

  function cancelReject() {
    setRejectingId(null);
    setReviewerNote('');
  }

  function confirmReject(id: string) {
    if (!reviewerNote.trim()) {
      return;
    }
    review.mutate(
      { id, status: 'rejected', reviewerNote: reviewerNote.trim() },
      {
        onSuccess: () => {
          showNotification('success', 'Submission rejected');
          setRejectingId(null);
          setReviewerNote('');
        },
        onError: () => showNotification('error', 'Failed to reject submission'),
      },
    );
  }

  function handleDownload(id: string) {
    download.mutate(id, {
      onSuccess: (result) => {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      },
      onError: () => showNotification('error', 'Failed to fetch download URL'),
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Regulatory Approvals</h1>
        <p className="text-sm text-text-muted mt-0.5">
          Cross-tenant regulatory bundle submissions awaiting review
          {data && <span className="ml-1 font-medium text-warning">({data.length} pending)</span>}
        </p>
      </div>

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 font-medium text-text-muted">Organization</th>
              <th className="px-4 py-3 font-medium text-text-muted">Country</th>
              <th className="px-4 py-3 font-medium text-text-muted">Document type</th>
              <th className="px-4 py-3 font-medium text-text-muted">Status</th>
              <th className="px-4 py-3 font-medium text-text-muted">Submitted</th>
              <th className="px-4 py-3 font-medium text-text-muted">Document</th>
              <th className="px-4 py-3 font-medium text-text-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && data?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                  No pending regulatory submissions found.
                </td>
              </tr>
            )}
            {data?.map((submission) => {
              const isRejecting = rejectingId === submission.id;
              const isResolving = review.isPending;

              return (
                <tr
                  key={submission.id}
                  className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors"
                >
                  <td className="px-4 py-3 text-text-secondary font-mono text-xs">
                    {submission.organizationId}
                  </td>
                  <td className="px-4 py-3 font-medium text-text">{submission.country}</td>
                  <td className="px-4 py-3 text-text-secondary">{submission.documentType}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusBadgeVariant(submission.status)}>
                      {submission.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-xs">
                    {formatSubmittedAt(submission.submittedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleDownload(submission.id)}
                      disabled={download.isPending}
                      aria-label="Download document"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-text-secondary hover:text-text hover:bg-surface-hover disabled:opacity-40 transition-colors text-xs"
                    >
                      <Download size={14} />
                      Download
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {!isRejecting && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleApprove(submission.id)}
                          disabled={isResolving}
                          aria-label="Approve"
                          className="p-1.5 rounded-md border border-success/40 text-success hover:bg-success/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => startReject(submission.id)}
                          disabled={isResolving}
                          aria-label="Reject"
                          className="p-1.5 rounded-md border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    {isRejecting && (
                      <div className="flex flex-col gap-2 min-w-[220px]">
                        <label
                          htmlFor={`reviewer-note-${submission.id}`}
                          className="text-xs text-text-muted"
                        >
                          Reviewer note
                        </label>
                        <textarea
                          id={`reviewer-note-${submission.id}`}
                          value={reviewerNote}
                          onChange={(e) => setReviewerNote(e.target.value)}
                          placeholder="Reason for rejection (required)"
                          rows={2}
                          className="w-full bg-background border border-border rounded-md px-2 py-1 text-xs text-text focus:outline-none focus:border-primary"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => confirmReject(submission.id)}
                            disabled={!reviewerNote.trim() || isResolving}
                            className="px-2 py-1 rounded-md border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs"
                          >
                            Confirm reject
                          </button>
                          <button
                            type="button"
                            onClick={cancelReject}
                            className="px-2 py-1 rounded-md border border-border text-text-muted hover:text-text hover:bg-surface-hover transition-colors text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {notification && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border text-sm shadow-lg z-50 ${
            notification.type === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
}
